import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  FREE_BOOKING_PROCESS_NAME,
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

import { getListingsById, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { clearCouponState, initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import { toggleFavorite, isFavorite } from '../FavouritePage/FavouritePage.duck';

// Shared components
import {
  H4,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
  Icons,
  H3,
  PublicMoorageCheckInModal,
  ExternalLink,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
  showListing,
  fetchReviews,
  uploadPublicReviewImage,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
  priceForSchemaMaybe,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionReviews from './SectionReviews';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import SectionGallery from './SectionGallery';
import CustomListingFields from './CustomListingFields';
import tagGif from '../../assets/tag-icon.gif';

import css from './ListingPage.module.css';
import {
  calculateFinalPrice,
  isArrayLength,
  isInstantBooking,
  isThisPropertyCharitable,
} from '../../util/genericHelpers';
import { isPublicMoorageListing } from '../../util/listingHelpers';
import {
  formatCheckInAge,
  getActivePublicMoorageCheckIn,
  isCheckInActive,
} from '../../util/publicMoorageCheckIn';
import { fetchActivePublicMoorageCheckInForCurrentUser } from '../../util/api.js';
import PopularMoorage from './SimilarMoorage';
import { withViewport } from '../../util/uiHelpers.js';
import {
  getBoatPrice,
  getEffectiveTransactionProcessAlias,
  getPriceAndAssignListingType,
  validateCustomListingPrice,
} from '../../util/dataExtractors.js';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import PublicMoorageActionSection from './PublicMoorageActionSection.js';
import useDamageWaiverConfig from '../../hooks/useDamageWaiverConfig';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

export const getListingPageActivePublicMoorageCheckIn = (
  listing,
  localCheckIn,
  listingId,
  currentUser
) => {
  const activeLocalCheckIn =
    localCheckIn?.listingId === listingId && isCheckInActive(localCheckIn) ? localCheckIn : null;
  return activeLocalCheckIn || getActivePublicMoorageCheckIn(listing, currentUser);
};

const normalizeInspectorName = value =>
  String(value || '')
    .trim()
    .replace(/\s+/g, ' ');

const toBoolean = value => value === true || value === 'true';

const safeExternalUrl = value => {
  const url = String(value || '').trim();

  if (!url) {
    return '';
  }

  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

const getListingInspector = publicData => {
  const name = normalizeInspectorName(publicData?.inspectorName);

  if (!name) {
    return null;
  }

  return {
    name,
    isPublished: toBoolean(publicData?.inspectorIsPublished),
    logoUrl: String(publicData?.inspectorLogoUrl || '').trim(),
    websiteUrl: safeExternalUrl(publicData?.inspectorWebsiteUrl),
  };
};

const InspectorBadge = props => {
  const { inspector } = props;
  const showLogo = inspector?.isPublished && inspector?.logoUrl;
  const showLearnMore = inspector?.isPublished && inspector?.websiteUrl;

  return (
    <div className={css.inspectionContainer}>
      <div className={css.inspectionLabel}>
        <FormattedMessage id="ListingPage.inspector.heading" />
      </div>

      {showLogo ? (
        <img className={css.inspectorLogo} src={inspector.logoUrl} alt={`${inspector.name} logo`} />
      ) : (
        <div className={css.inspectorName}>{inspector.name}</div>
      )}

      {showLearnMore ? (
        <div className={css.learnMore}>
          <ExternalLink href={inspector.websiteUrl} className={css.learnMoreLink}>
            <FormattedMessage id="ListingPage.inspector.learnMore" />
          </ExternalLink>
        </div>
      ) : null}
    </div>
  );
};

export const ListingPageComponent = props => {
  const damageWaiverConfig = useDamageWaiverConfig();
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [checkInModalOpen, setCheckInModalOpen] = useState(false);
  const [localPublicMoorageCheckIn, setLocalPublicMoorageCheckIn] = useState(null);
  const [mounted, setMounted] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    onFetchListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    monthlyTimeSlots,
    onFetchTimeSlots,
    onFetchTransactionLineItems,
    onFetchReviews,
    onUploadPublicReviewImage,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    onClearCouponState,
    config,
    routeConfiguration,
    similarListings,
    viewport,
    showOwnListingsOnly,
  } = props;

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  useEffect(() => {
    setLocalPublicMoorageCheckIn(null);
  }, [listingId.uuid]);

  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));
  const isCurrentListingPublicMoorage = isPublicMoorageListing(
    currentListing.attributes?.publicData
  );

  useEffect(() => {
    if (!isCurrentListingPublicMoorage || !isAuthenticated || !listingId.uuid) {
      return undefined;
    }

    let isMounted = true;
    fetchActivePublicMoorageCheckInForCurrentUser()
      .then(response => {
        if (!isMounted) return;
        const checkIn = response?.result?.checkIn || null;
        setLocalPublicMoorageCheckIn(checkIn?.listingId === listingId.uuid ? checkIn : null);
      })
      .catch(error => {
        console.error('Failed to load active public moorage check-in:', error);
      });

    return () => {
      isMounted = false;
    };
  }, [isCurrentListingPublicMoorage, isAuthenticated, listingId.uuid]);

  const isListingFavorite = useSelector(state => isFavorite(state, listingId.uuid));
  const handleFavoriteClick = e => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(toggleFavorite(listingId.uuid));
  };

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'description';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const transactionProcessAlias = publicData?.transactionProcessAlias;
  const effectiveProcessAlias =
    getEffectiveTransactionProcessAlias(currentListing) || transactionProcessAlias;
  const processName = resolveLatestProcessName(effectiveProcessAlias?.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking ? 'booking' : isPurchase ? 'purchase' : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing &&
    processType !== 'inquiry' &&
    processName !== FREE_BOOKING_PROCESS_NAME &&
    !currentUser?.attributes?.stripeConnected;
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
    onClearCouponState,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

  const isPublicMoorage = isCurrentListingPublicMoorage;
  const activePublicMoorageCheckIn = getListingPageActivePublicMoorageCheckIn(
    currentListing,
    localPublicMoorageCheckIn,
    listingId.uuid,
    currentUser
  );
  const activeCheckInAge = formatCheckInAge(activePublicMoorageCheckIn);
  const handlePublicMoorageCheckInSuccess = result => {
    const checkIn = result?.checkIn;
    if (checkIn?.listingId === listingId.uuid) {
      setLocalPublicMoorageCheckIn(checkIn);
      onFetchListing?.(listingId, config);
    }
  };
  const openPublicCheckInModal = () => {
    if (!isAuthenticated) {
      const from = `${location.pathname}${location.search}${location.hash || ''}`;
      const loginPath = createResourceLocatorString('LoginPage', routeConfiguration, {}, {});
      history.push({ pathname: loginPath, state: { from } });
      return;
    }
    setCheckInModalOpen(true);
  };

  const customPrice = validateCustomListingPrice(price);

  const isPropertyCharitable = isThisPropertyCharitable(currentListing);
  const listingInspector = getListingInspector(publicData);
  const infoOnly = publicData?.infoOnly || false;
  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(price),
          // ...availabilityMaybe,
        },
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={css.listingPageWrapper}>
          <div className={css.contentWidth}>
            <div className={css.topActionsContainer}>
              {currentListing.id ? (
                <ActionBarMaybe
                  className={css.actionBarForProductLayout}
                  isOwnListing={isOwnListing}
                  listing={currentListing}
                  showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                  currentUser={currentUser}
                  editParams={{
                    id: listingId.uuid,
                    slug: listingSlug,
                    type: listingPathParamType,
                    tab: listingTab,
                  }}
                />
              ) : null}

              {/* Heart Icon for Favorites */}
              {!isOwnListing && currentUser && (
                <button
                  className={css.favoriteButton}
                  onClick={handleFavoriteClick}
                  aria-label={isListingFavorite ? 'Remove from favorites' : 'Add to favorites'}
                >
                  <Icons
                    name={isListingFavorite ? 'heartFilled' : 'heart'}
                    className={css.heartIcon}
                  />
                </button>
              )}
            </div>
            <div className={css.imageContainer}>
              <SectionGallery
                listing={currentListing}
                viewport={viewport}
                variantPrefix={config.layout.listingImage.variantPrefix}
                isListingPage={true}
              />
              {!isPublicMoorage && isInstantBooking(currentListing) ? (
                <div className={css.badge}>
                  <Icons name="instantBooking" />{' '}
                  <FormattedMessage id="ListingCard.instantBooking" />
                </div>
              ) : null}
              {isPublicMoorage ? (
                <div className={css.publicMoorageBadge}>Public Moorage</div>
              ) : null}
            </div>
            <div className={css.contentWrapperForProductLayout}>
              <div className={css.mainColumnForProductLayout}>
                <SectionTextMaybe
                  title={
                    <H4 as="h1" className={css.orderPanelTitle}>
                      <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />{' '}
                    </H4>
                  }
                  publicData={publicData}
                  listingConfig={listingConfig}
                />
                <CustomListingFields
                  publicData={publicData}
                  metadata={metadata}
                  listingFieldConfigs={listingConfig.listingFields}
                  categoryConfiguration={config.categoryConfiguration}
                  intl={intl}
                  description={description}
                />
                <SectionMapMaybe
                  geolocation={geolocation}
                  publicData={publicData}
                  listingId={currentListing.id}
                  mapsConfig={config.maps}
                />
              </div>
              <div className={css.orderColumnForProductLayout}>
                {!infoOnly && (
                  <div className={css.priceContainer}>
                    <img src={tagGif} alt="Tag icon" className={css.tagGif} />

                    <span>
                      {' '}
                      <FormattedMessage id="ListingPage.includeAllTaxes" />
                    </span>
                  </div>
                )}

                {!isPublicMoorage ? (
                  <div className={css.priceContainer}>
                    <>
                      <span className={css.price}>
                        {calculateFinalPrice(price?.amount, damageWaiverConfig)}
                      </span>{' '}
                      <span className={css.priceSuffix}>
                        <FormattedMessage id="ListingPage.perNight" />
                      </span>{' '}
                    </>
                  </div>
                ) : null}

                {isPublicMoorage ? (
                  <PublicMoorageActionSection
                    publicData={publicData}
                    activeCheckIn={activePublicMoorageCheckIn}
                    activeCheckInAge={activeCheckInAge}
                    onCheckIn={openPublicCheckInModal}
                  />
                ) : (
                  <OrderPanel
                    className={css.productOrderPanel}
                    listing={currentListing}
                    isOwnListing={isOwnListing}
                    currentUser={currentUser}
                    onSubmit={handleOrderSubmit}
                    customPrice={customPrice}
                    authorLink={
                      <NamedLink
                        className={css.authorNameLink}
                        name="ListingPage"
                        params={params}
                        to={{ hash: '#author' }}
                      >
                        {authorDisplayName}
                      </NamedLink>
                    }
                    title={
                      <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                    }
                    titleDesktop={
                      <H4 as="h1" className={css.orderPanelTitle}>
                        <FormattedMessage
                          id="ListingPage.orderTitle"
                          values={{ title: richTitle }}
                        />
                      </H4>
                    }
                    author={ensuredAuthor}
                    onManageDisableScrolling={onManageDisableScrolling}
                    onContactUser={onContactUser}
                    monthlyTimeSlots={monthlyTimeSlots}
                    onFetchTimeSlots={onFetchTimeSlots}
                    onFetchTransactionLineItems={onFetchTransactionLineItems}
                    lineItems={lineItems}
                    fetchLineItemsInProgress={fetchLineItemsInProgress}
                    fetchLineItemsError={fetchLineItemsError}
                    validListingTypes={config.listing.listingTypes}
                    marketplaceCurrency={config.currency}
                    dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
                    marketplaceName={config.marketplaceName}
                  />
                )}

                {isPublicMoorage ? (
                  <PublicMoorageCheckInModal
                    isOpen={checkInModalOpen}
                    onClose={() => setCheckInModalOpen(false)}
                    onCheckInSuccess={handlePublicMoorageCheckInSuccess}
                    listing={currentListing}
                    checkIn={activePublicMoorageCheckIn}
                  />
                ) : null}

                {isPropertyCharitable && (
                  <div className={css.charityContainer}>
                    <h5 className={css.charityHeading}>
                      <FormattedMessage id="ListingPage.charity.heading" />{' '}
                    </h5>
                    <img className={css.charityLogo} src={isPropertyCharitable?.foundationLogo} />
                    <h5 className={css.charityHeading}>
                      <FormattedMessage id="ListingPage.charity.description" />
                    </h5>
                    <div className={css.learnMore}>
                      <NamedLink
                        name="SupportDetailPage"
                        params={{
                          slug: 'general-faqs',
                          id: '4',
                          title: 'what-marine-conservation-organizations-does-onbuoy-support',
                        }}
                      >
                        <FormattedMessage id="ListingPage.charity.learnMore" />
                      </NamedLink>
                    </div>
                  </div>
                )}

                {listingInspector ? <InspectorBadge inspector={listingInspector} /> : null}
              </div>
            </div>
          </div>
          <div className={css.fullWidthContent}>
            <div className={css.contentWidth}>
              <SectionReviews
                listing={currentListing}
                imageReviews={publicData?.imageReviews}
                reviews={reviews}
                fetchReviewsError={fetchReviewsError}
                onManageDisableScrolling={onManageDisableScrolling}
                currentUser={currentUser}
                isAuthenticated={isAuthenticated}
                onUploadPublicReviewImage={onUploadPublicReviewImage}
                onReviewCreated={() => onFetchReviews?.(listingId.uuid)}
              />
              <SectionAuthorMaybe
                title={title}
                listing={currentListing}
                authorDisplayName={authorDisplayName}
                onContactUser={onContactUser}
                isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
                onCloseInquiryModal={() => setInquiryModalOpen(false)}
                sendInquiryError={sendInquiryError}
                sendInquiryInProgress={sendInquiryInProgress}
                onSubmitInquiry={onSubmitInquiry}
                currentUser={currentUser}
                onManageDisableScrolling={onManageDisableScrolling}
              />
            </div>
          </div>
          {isArrayLength(similarListings) ? <PopularMoorage listings={similarListings} /> : null}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

ListingPageComponent.defaultProps = {
  currentUser: null,
  inquiryModalOpenForListingId: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: null,
  sendInquiryError: null,
  lineItems: null,
  fetchLineItemsError: null,
};

const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  // if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
  //   return (
  //     <NamedRedirect
  //       name="NoAccessPage"
  //       params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
  //     />
  //   );
  // } else if (
  //   (hasNoViewingRights && isForbiddenError(showListingError)) ||
  //   isErrorNoViewingPermission(showListingError)
  // ) {
  //   // If the user has no viewing rights, fetching anything but their own listings
  //   // will return a 403 error. If that happens, redirect to NoAccessPage.
  //   return (
  //     <NamedRedirect
  //       name="NoAccessPage"
  //       params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
  //     />
  //   );
  // }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    similarListingsIds,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const similarListings =
    isArrayLength(similarListingsIds) && getListingsById(state, similarListingsIds);

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendInquiryInProgress,
    sendInquiryError,
    similarListings,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)),
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onClearCouponState: () => dispatch(clearCouponState()),
  onFetchListing: (listingId, config) => dispatch(showListing(listingId, config)),
  onFetchReviews: listingId => dispatch(fetchReviews(listingId)),
  onUploadPublicReviewImage: file => dispatch(uploadPublicReviewImage(file)),
  onFetchTimeSlots: (listingId, start, end, timeZone) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withViewport
)(EnhancedListingPage);

export default ListingPage;
